import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";

export function LoginPage() {
	const { session, loading } = useAuth();
	const navigate = useNavigate();

	if (!loading && session) {
		return <Navigate to="/" replace />;
	}

	const handleGoogleSignIn = async () => {
		await supabase.auth.signInWithOAuth({
			provider: "google",
			options: {
				redirectTo: `${window.location.origin}/`,
			},
		});
	};

	const handleDemoSignIn = async () => {
		const { error } = await supabase.auth.signInAnonymously();
		if (error) {
			console.error("Demo sign in error:", error.message);
		} else {
			navigate("/");
		}
	};

	if (loading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-surface-container-high">
				<div className="font-label-bold text-on-surface uppercase animate-pulse">
					Loading...
				</div>
			</div>
		);
	}

	return (
		<div className="bg-surface-container-high text-on-surface min-h-screen flex items-center justify-center p-5 relative overflow-hidden">
			{/* Background decorations */}
			<div className="absolute top-10 left-10 w-32 h-32 bg-secondary-container border-4 border-brand-black rounded-full shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] -z-10" />
			<div className="absolute bottom-20 right-20 w-48 h-48 bg-primary-container border-4 border-brand-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transform rotate-12 -z-10" />
			<div className="absolute top-1/4 right-1/4 w-16 h-16 bg-error-container border-4 border-brand-black rounded-full shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] -z-10" />

			<main className="w-full max-w-4xl bg-surface-container-lowest border-4 border-brand-black shadow-[16px_16px_0px_0px_rgba(0,0,0,1)] grid grid-cols-1 md:grid-cols-2">
				{/* Left side — branding + illustration */}
				<div className="bg-secondary-container border-b-4 md:border-b-0 md:border-r-4 border-brand-black p-8 flex flex-col items-center justify-center relative overflow-hidden min-h-[400px]">
					<div className="flex flex-col items-center justify-center flex-grow">
						{/* Brand name title */}
						<h1 className="text-[48px] leading-[1.1] tracking-[-0.04em] font-bold text-on-surface italic mb-8 text-center font-[Space_Grotesk] relative">
							ENERGY
						</h1>

						{/* Central illustration */}
						<div className="w-48 h-48 border-4 border-brand-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] bg-brand-orange flex items-center justify-center mb-6">
							<span
								className="material-symbols-outlined text-[80px] text-brand-black"
								style={{ fontVariationSettings: "'FILL' 1" }}
							>
								bolt
							</span>
						</div>
					</div>

					{/* Sticker tag */}
					<div className="mt-auto bg-brand-white border-2 border-brand-black px-4 py-2 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] rotate-[-5deg]">
						<span className="font-label-bold text-[14px] leading-none font-bold font-[Space_Grotesk] uppercase">
							Stay Caffeinated
						</span>
					</div>
				</div>

				{/* Right side — login form */}
				<div className="p-8 flex flex-col justify-center bg-brand-white">
					<div className="mb-8 text-center md:text-left">
						<h2 className="font-headline-lg text-[32px] leading-[1.2] tracking-[-0.02em] font-bold font-[Space_Grotesk] uppercase mb-2">
							Get the Rush
						</h2>
						<p className="font-body-md text-[16px] leading-[1.5] text-on-surface-variant">
							Log in to track, rate, and discover your next favorite energy
							drink.
						</p>
					</div>
					<div className="space-y-4">
						<button
							onClick={handleGoogleSignIn}
							className="w-full py-4 px-6 bg-brand-white border-[3px] border-brand-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] flex items-center justify-center gap-3 hover:bg-surface-container-high hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all active:translate-x-[6px] active:translate-y-[6px] active:shadow-none"
						>
							<svg
								className="w-6 h-6"
								fill="none"
								viewBox="0 0 24 24"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
									fill="#4285F4"
								/>
								<path
									d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
									fill="#34A853"
								/>
								<path
									d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
									fill="#FBBC05"
								/>
								<path
									d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
									fill="#EA4335"
								/>
							</svg>
							<span className="font-label-bold text-[14px] leading-none font-bold font-[Space_Grotesk] uppercase">
								Continue with Google
							</span>
						</button>

						<div className="relative flex items-center py-4">
							<div className="flex-grow border-t-2 border-brand-black" />
							<span className="flex-shrink-0 mx-4 font-body-md text-[16px] leading-[1.5] uppercase font-bold text-on-surface-variant">
								Or
							</span>
							<div className="flex-grow border-t-2 border-brand-black" />
						</div>

						<button
							onClick={handleDemoSignIn}
							className="w-full py-4 px-6 bg-primary-container text-on-primary border-4 border-brand-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] font-headline-md text-[24px] leading-[1.2] font-bold font-[Space_Grotesk] uppercase hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-all active:translate-x-[8px] active:translate-y-[8px] active:shadow-none"
						>
							TRY DEMO
						</button>
					</div>
				</div>
			</main>
		</div>
	);
}
